// «Editar» en la ficha del vendedor: nombre, teléfono y tipo de empleo, más su wallet PCF
// (vincular, cambiar o desvincular). El correo y la tienda se ven pero no se editan aquí.
import { useId, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Button, ConfirmDialog, Input, PhoneInput, Skeleton } from '@/kit';
import {
    useLinkRetailUserWallet,
    useUnlinkRetailUserWallet,
    useUpdateRetailUser,
    type RetailUser,
} from '@/domains/retail/negocios/api/useRetailOrg';
import { WalletLinkField } from '@/domains/retail/negocios/components/WalletLinkField';
import {
    linkedWalletText,
    localMxPhone,
    type WalletFieldState,
} from '@/domains/retail/negocios/components/walletLink';
import { EMPLOYMENT_OPTIONS } from '@/domains/retail/negocios/lib/employmentOptions';
import {
    EditorDrawer,
    EditorFooter,
    FormSection,
    FormSelect,
} from '../../inventario/components/catalog/FormBits';
import {
    toRosterWriteError,
    useInvalidateRoster,
    useRosterAbilities,
} from '../../lib/rosterWrites';
import { useRosterUser } from '../../lib/useRoster';
import {
    editFormOf,
    editSellerSchema,
    toUpdateRequest,
    type EditSellerValues,
} from '../lib/editSellerForm';
import { UNNAMED_SELLER, type SellerRow } from '../lib/sellersTable';
import {
    IntegrationLockNotice,
    useIntegrationLock,
} from '../../integration/components/IntegrationLock';

const UNVERIFIED_WALLET = 'Verifica la wallet o borra la CLABE para guardar sin cambiarla.';

function WalletStatus({
    user,
    canUnlink,
    onUnlink,
}: {
    user: RetailUser;
    canUnlink: boolean;
    onUnlink: () => void;
}) {
    const current = linkedWalletText(user);
    return (
        <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/30 px-3 py-2 text-[12px]">
            {current ? (
                <span>
                    <b>Vinculada:</b> {current}
                </span>
            ) : (
                <span>
                    <b>Sin wallet</b>
                    <span className="text-muted-foreground">
                        {' '}
                        · sus comisiones quedan pendientes hasta vincularla.
                    </span>
                </span>
            )}
            {current && canUnlink ? (
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-6 flex-none px-2 text-[11px] text-danger"
                    onClick={onUnlink}
                >
                    Desvincular
                </Button>
            ) : null}
        </div>
    );
}

function SellerEditor({
    row,
    retailerId,
    user,
    onClose,
}: {
    row: SellerRow;
    retailerId: string;
    user: RetailUser;
    onClose: () => void;
}) {
    const formId = useId();
    const update = useUpdateRetailUser(retailerId);
    const link = useLinkRetailUserWallet(retailerId);
    const unlink = useUnlinkRetailUserWallet(retailerId);
    const { locked } = useIntegrationLock();
    const invalidateRoster = useInvalidateRoster();
    const [error, setError] = useState<string | null>(null);
    const [wallet, setWallet] = useState<WalletFieldState>({ kind: 'empty' });
    const [walletError, setWalletError] = useState<string | null>(null);
    const [confirmUnlink, setConfirmUnlink] = useState(false);
    const [unlinkError, setUnlinkError] = useState<string | null>(null);
    // Con los datos ya guardados, el siguiente envío solo vuelve a intentar vincular la wallet.
    const [dataSaved, setDataSaved] = useState(false);
    const hadPhone = (user.phone ?? '').trim() !== '';
    const fallbackName = row.name === UNNAMED_SELLER ? '' : row.name;
    const original = editFormOf(user, fallbackName);
    const form = useForm<EditSellerValues>({
        resolver: zodResolver(
            editSellerSchema({
                hadPhone,
                originalPhone: original.phone,
                originalName: original.displayName,
            }),
        ),
        defaultValues: original,
    });
    const { errors, isSubmitting } = form.formState;
    const busy = isSubmitting || update.isPending || link.isPending || unlink.isPending;
    const name = user.displayName ?? row.name;

    const submit = async (values: EditSellerValues) => {
        setError(null);
        if (wallet.kind === 'unverified') {
            setWalletError(UNVERIFIED_WALLET);
            return;
        }
        // Con la cadena integrada solo se guarda la wallet; los datos los manda Zentral.
        const body = dataSaved || locked ? null : toUpdateRequest(values, user, fallbackName);
        if (body === null && wallet.kind !== 'verified') {
            if (dataSaved) {
                toast.success('Datos guardados; la wallet quedó sin cambios.', {
                    description: name,
                });
            }
            onClose();
            return;
        }
        if (body !== null) {
            try {
                await update.mutateAsync({ retailUserId: user.retailUserId, body });
            } catch (failure) {
                const mapped = toRosterWriteError(failure);
                if (mapped.field === 'phone') {
                    form.setError('phone', { message: mapped.message });
                }
                setError(mapped.message);
                return;
            }
            setDataSaved(true);
            void invalidateRoster();
        }
        if (wallet.kind === 'verified') {
            try {
                await link.mutateAsync({ retailUserId: user.retailUserId, body: wallet.body });
            } catch (failure) {
                const reason = toRosterWriteError(failure).message;
                const message =
                    body !== null || dataSaved
                        ? `Los datos se guardaron, pero la wallet no se vinculó: ${reason}`
                        : `La wallet no se vinculó: ${reason}`;
                setError(message);
                return;
            }
        }
        await invalidateRoster();
        toast.success('Cambios guardados', { description: name });
        onClose();
    };

    const removeWallet = async () => {
        setUnlinkError(null);
        try {
            await unlink.mutateAsync(user.retailUserId);
        } catch (failure) {
            setUnlinkError(toRosterWriteError(failure).message);
            return;
        }
        setConfirmUnlink(false);
        toast.success('Wallet desvinculada', {
            description: 'Sus comisiones quedan pendientes hasta vincular otra.',
        });
        await invalidateRoster();
    };

    return (
        <EditorDrawer
            open
            onClose={() => {
                if (!busy) onClose();
            }}
            title={`Editar · ${name}`}
            footer={
                <EditorFooter
                    formId={formId}
                    submitLabel={
                        dataSaved && wallet.kind === 'verified'
                            ? 'Vincular wallet'
                            : 'Guardar cambios'
                    }
                    isPending={busy}
                    error={error}
                    onCancel={onClose}
                />
            }
        >
            <form
                id={formId}
                noValidate
                autoComplete="off"
                aria-label={`Editar ${name}`}
                className="flex flex-col gap-5"
                onSubmit={(event) => {
                    void form.handleSubmit(submit)(event);
                }}
            >
                <fieldset disabled={dataSaved || busy || locked} className="contents">
                    <FormSection title="Datos">
                        <IntegrationLockNotice message="lockedSellerData" />
                        <Input
                            label="Nombre"
                            required
                            error={errors.displayName?.message}
                            {...form.register('displayName')}
                        />
                        <div className="grid grid-cols-2 gap-3">
                            <Controller
                                control={form.control}
                                name="phone"
                                render={({ field, fieldState }) => (
                                    <PhoneInput
                                        ref={field.ref}
                                        name={field.name}
                                        label="Teléfono"
                                        value={field.value}
                                        onValueChange={field.onChange}
                                        onBlur={field.onBlur}
                                        error={fieldState.error?.message}
                                    />
                                )}
                            />
                            <Controller
                                control={form.control}
                                name="employmentType"
                                render={({ field }) => (
                                    <FormSelect
                                        label="Tipo de empleo"
                                        value={field.value}
                                        onChange={field.onChange}
                                        options={EMPLOYMENT_OPTIONS}
                                    />
                                )}
                            />
                        </div>
                        <Input
                            label="Correo"
                            readOnly
                            value={row.email ?? ''}
                            placeholder="Sin registrar"
                            hint="Se cambia en su cuenta."
                        />
                    </FormSection>
                    <FormSection title="Tienda">
                        <Input
                            label="Tienda"
                            readOnly
                            value={row.storeName ?? ''}
                            placeholder="Sin tienda"
                            hint="Para cambiarla usa «Transferir»."
                        />
                    </FormSection>
                </fieldset>
                <FormSection title="Wallet PCF">
                    <WalletStatus
                        user={user}
                        canUnlink={!busy}
                        onUnlink={() => {
                            setUnlinkError(null);
                            setConfirmUnlink(true);
                        }}
                    />
                    <WalletLinkField
                        retailerId={retailerId}
                        hint="Elige una CLABE para vincular o cambiar la wallet; se aplica al guardar. Solo se guardan los últimos 4 dígitos."
                        suggestedPhone={localMxPhone(user.phone ?? '')}
                        error={walletError}
                        disabled={busy}
                        onStateChange={(next) => {
                            setWallet(next);
                            setWalletError(null);
                        }}
                    />
                </FormSection>
            </form>
            <ConfirmDialog
                open={confirmUnlink}
                onOpenChange={(next) => {
                    if (!next && !unlink.isPending) setConfirmUnlink(false);
                }}
                title={`¿Desvincular la wallet de ${name}?`}
                description="Sus comisiones quedarán pendientes hasta que se vincule otra wallet."
                confirmLabel="Desvincular"
                destructive
                loading={unlink.isPending}
                onConfirm={() => {
                    void removeWallet();
                }}
            >
                {unlinkError === null ? null : (
                    <p role="alert" className="text-[12px] text-danger">
                        {unlinkError}
                    </p>
                )}
            </ConfirmDialog>
        </EditorDrawer>
    );
}

/** Carga al usuario del padrón antes de armar el formulario, que arranca con sus datos. */
function SellerEditorLoader({
    row,
    retailerId,
    onClose,
}: {
    row: SellerRow;
    retailerId: string;
    onClose: () => void;
}) {
    const { user, isPending, isError } = useRosterUser(retailerId, row.sellerId);
    if (user !== null) {
        return <SellerEditor row={row} retailerId={retailerId} user={user} onClose={onClose} />;
    }
    return (
        <EditorDrawer
            open
            onClose={onClose}
            title={`Editar · ${row.name}`}
            footer={
                <div className="flex justify-end">
                    <Button type="button" variant="outline" onClick={onClose}>
                        Cerrar
                    </Button>
                </div>
            }
        >
            {isPending ? (
                <div className="flex flex-col gap-3" aria-busy>
                    <Skeleton className="h-9 w-full" />
                    <Skeleton className="h-9 w-full" />
                    <Skeleton className="h-9 w-full" />
                </div>
            ) : (
                <p role="alert" className="text-[12px] text-danger">
                    {isError
                        ? 'No pudimos cargar los datos del vendedor. Intenta de nuevo.'
                        : 'No se encontró a este vendedor en la cadena.'}
                </p>
            )}
        </EditorDrawer>
    );
}

/** Sin `retail.user.update`, sin una sola cadena o sin padrón no se pinta nada (y no se monta ningún hook de consulta). */
export function EditSellerButton({
    row,
    retailerId,
}: {
    row: SellerRow;
    retailerId: string | null;
}) {
    const { canUpdateUser } = useRosterAbilities();
    const [openCount, setOpenCount] = useState(0);
    const [open, setOpen] = useState(false);
    if (!canUpdateUser || retailerId === null || row.status === null) return null;
    return (
        <>
            <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-6 px-2 text-[11px]"
                leftIcon={<Pencil className="size-3" aria-hidden />}
                onClick={() => {
                    setOpenCount((count) => count + 1);
                    setOpen(true);
                }}
            >
                Editar
            </Button>
            {open ? (
                <SellerEditorLoader
                    key={openCount}
                    row={row}
                    retailerId={retailerId}
                    onClose={() => {
                        setOpen(false);
                    }}
                />
            ) : null}
        </>
    );
}
